// Board templates. v1 ships 7 built-ins as code constants; user-saved
// templates live in localStorage on the client (the SQLite-backed
// `templates` table from spec §10 returns post-launch).
//
// Each template is a small bundle: metadata (id/name/description) plus
// the shapes to stamp into a fresh board. Template selection at board-
// creation time hands the chosen id to the new board page, which seeds
// the doc with `template.shapes` on the first paint.
//
// Built-ins use deterministic shape ids (`tpl-{kind}-{ix}`) so two boards
// created from the same template don't ship the SAME ids — the client
// remaps them through `instantiateTemplateShapes` at seed time, which
// assigns fresh ids and patches arrow endpoint refs.

import type { Shape, Sticky, Frame } from './shapes.js';

export type TemplateKind =
  | 'blank'
  | 'start-stop-continue'
  | '4ls'
  | 'mad-sad-glad'
  | 'kalm'
  | 'mindmap'
  | 'systemmap'
  | 'user';

export interface TemplateMeta {
  id: string;
  kind: TemplateKind;
  name: string;
  description: string;
  // Optional one-line "what this is for" hint shown on the picker thumbnail.
  hint?: string;
  // ISO timestamp for user-saved templates; absent on built-ins.
  createdAt?: number;
}

export interface Template extends TemplateMeta {
  shapes: Shape[];
}

// Built-in templates always use the same id; user-saved templates use a
// `user-{epoch}` id stamped at save time.
export const BUILT_IN_TEMPLATE_IDS = {
  blank: 'builtin-blank',
  ssc: 'builtin-ssc',
  fourLs: 'builtin-4ls',
  madSadGlad: 'builtin-mad-sad-glad',
  kalm: 'builtin-kalm',
  mindmap: 'builtin-mindmap',
  systemmap: 'builtin-systemmap',
} as const;

// ---------------------------------------------------------------------------
// Shape factories — kept tiny so the template definitions read like data.
// ---------------------------------------------------------------------------

interface FrameOpts {
  id: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
  label: string;
}

function frame({ id, x, y, w = 320, h = 480, label }: FrameOpts): Frame {
  return {
    id,
    type: 'frame',
    x,
    y,
    w,
    h,
    label,
    authorId: 'template',
    createdAt: 0,
  };
}

interface StickyOpts {
  id: string;
  x: number;
  y: number;
  color?: Sticky['color'];
  text?: string;
}

function sticky({ id, x, y, color = 'sand', text = '' }: StickyOpts): Sticky {
  return {
    id,
    type: 'sticky',
    x,
    y,
    w: 200,
    h: 160,
    color,
    text,
    authorId: 'template',
    createdAt: 0,
  };
}

// Generate N column frames laid out horizontally with consistent gap + label.
function columnFrames(
  prefix: string,
  labels: readonly string[],
  opts: { y?: number; w?: number; h?: number; gap?: number; startX?: number } = {},
): Frame[] {
  const { y = 80, w = 320, h = 480, gap = 40, startX = 80 } = opts;
  return labels.map((label, i) =>
    frame({
      id: `${prefix}-${i + 1}`,
      x: startX + i * (w + gap),
      y,
      w,
      h,
      label,
    }),
  );
}

// ---------------------------------------------------------------------------
// Built-in templates
// ---------------------------------------------------------------------------

const BLANK: Template = {
  id: BUILT_IN_TEMPLATE_IDS.blank,
  kind: 'blank',
  name: 'Blank',
  description: 'An empty board.',
  hint: 'Start from scratch.',
  shapes: [],
};

const SSC: Template = {
  id: BUILT_IN_TEMPLATE_IDS.ssc,
  kind: 'start-stop-continue',
  name: 'Start / Stop / Continue',
  description: 'The classic 3-column retro.',
  hint: 'What to begin, end, and keep doing.',
  shapes: columnFrames('tpl-ssc-frame', ['Start', 'Stop', 'Continue']),
};

const FOUR_LS: Template = {
  id: BUILT_IN_TEMPLATE_IDS.fourLs,
  kind: '4ls',
  name: '4 Ls',
  description: 'Liked · Learned · Lacked · Longed for.',
  hint: 'Reflection-heavy retro for teams that want to dig deeper.',
  shapes: columnFrames('tpl-4ls-frame', ['Liked', 'Learned', 'Lacked', 'Longed for'], {
    w: 280,
  }),
};

const MAD_SAD_GLAD: Template = {
  id: BUILT_IN_TEMPLATE_IDS.madSadGlad,
  kind: 'mad-sad-glad',
  name: 'Mad / Sad / Glad',
  description: 'Emotion-first retro.',
  hint: 'Surface how the iteration felt, then dig into why.',
  shapes: columnFrames('tpl-msg-frame', ['Mad', 'Sad', 'Glad']),
};

const KALM: Template = {
  id: BUILT_IN_TEMPLATE_IDS.kalm,
  kind: 'kalm',
  name: 'KALM',
  description: 'Keep · Add · Less of · More of.',
  hint: 'Calibrate the dial on existing practices.',
  shapes: columnFrames('tpl-kalm-frame', ['Keep', 'Add', 'Less of', 'More of'], {
    w: 280,
  }),
};

// Mind map: central node + 6 radial branches, each a sticky with a sample
// branch label. Connections are NOT drawn (arrow-binding is v1.1) — users
// add arrows themselves with the Arrow tool. The radial geometry alone
// reads as a mind map to anyone who's used one.
const MINDMAP: Template = {
  id: BUILT_IN_TEMPLATE_IDS.mindmap,
  kind: 'mindmap',
  name: 'Mind map',
  description: 'A central topic with branches.',
  hint: 'Brainstorm radially around a single seed.',
  shapes: (() => {
    const cx = 600;
    const cy = 360;
    const r = 260;
    const root = sticky({
      id: 'tpl-mm-root',
      x: cx - 100,
      y: cy - 60,
      color: 'sand',
      text: 'Central topic',
    });
    const branches: Sticky[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
      branches.push(
        sticky({
          id: `tpl-mm-branch-${i + 1}`,
          x: cx + Math.cos(angle) * r - 100,
          y: cy + Math.sin(angle) * r - 60,
          color: 'fog',
        }),
      );
    }
    return [root, ...branches];
  })(),
};

// System map: 3 rect nodes left-to-right + 2 arrows linking them. Same
// arrow caveat as mind map — endpoints are `point`-kind so they stay
// anchored to absolute coords, not the rect ids. Bindings ship v1.1.
const SYSTEMMAP: Template = {
  id: BUILT_IN_TEMPLATE_IDS.systemmap,
  kind: 'systemmap',
  name: 'System map',
  description: 'Boxes-and-arrows starter.',
  hint: 'Sketch a system before formalizing it.',
  shapes: [
    {
      id: 'tpl-sm-rect-1',
      type: 'rect',
      x: 120,
      y: 280,
      w: 200,
      h: 100,
      fill: '#ffffff',
      stroke: '#1a1a1a',
      text: 'Input',
      authorId: 'template',
      createdAt: 0,
    },
    {
      id: 'tpl-sm-rect-2',
      type: 'rect',
      x: 420,
      y: 280,
      w: 200,
      h: 100,
      fill: '#ffffff',
      stroke: '#1a1a1a',
      text: 'Process',
      authorId: 'template',
      createdAt: 0,
    },
    {
      id: 'tpl-sm-rect-3',
      type: 'rect',
      x: 720,
      y: 280,
      w: 200,
      h: 100,
      fill: '#ffffff',
      stroke: '#1a1a1a',
      text: 'Output',
      authorId: 'template',
      createdAt: 0,
    },
    {
      id: 'tpl-sm-arrow-1',
      type: 'arrow',
      x: 0,
      y: 0,
      from: { kind: 'point', x: 320, y: 330 },
      to: { kind: 'point', x: 420, y: 330 },
      authorId: 'template',
      createdAt: 0,
    },
    {
      id: 'tpl-sm-arrow-2',
      type: 'arrow',
      x: 0,
      y: 0,
      from: { kind: 'point', x: 620, y: 330 },
      to: { kind: 'point', x: 720, y: 330 },
      authorId: 'template',
      createdAt: 0,
    },
  ],
};

export const BUILT_IN_TEMPLATES: readonly Template[] = [
  BLANK,
  SSC,
  FOUR_LS,
  MAD_SAD_GLAD,
  KALM,
  MINDMAP,
  SYSTEMMAP,
];

export function findBuiltInTemplate(id: string): Template | null {
  return BUILT_IN_TEMPLATES.find((t) => t.id === id) ?? null;
}

// ---------------------------------------------------------------------------
// Instantiation — strip the template's deterministic ids and stamp fresh
// ones so two boards created from the same template don't collide. Patches
// any arrow endpoints that referred to the old ids.
// ---------------------------------------------------------------------------

export interface InstantiateOpts {
  authorId?: string;
  // Optional override for the id generator — tests pin it for determinism.
  newId?: (oldId: string) => string;
}

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

function defaultNewId(_oldId: string): string {
  let out = '';
  for (let i = 0; i < 10; i++) out += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return out;
}

export function instantiateTemplateShapes(
  template: Template,
  opts: InstantiateOpts = {},
): Shape[] {
  const author = opts.authorId ?? 'template';
  const idGen = opts.newId ?? defaultNewId;
  const remap = new Map<string, string>();
  for (const s of template.shapes) remap.set(s.id, idGen(s.id));
  return template.shapes.map((shape) => {
    const next: Record<string, unknown> = { ...shape };
    next.id = remap.get(shape.id);
    next.authorId = author;
    next.createdAt = Date.now();
    // Arrow endpoints: shape-kind references must be remapped. point-kind
    // endpoints (the v1 default) are absolute coords, no remap needed.
    if (shape.type === 'arrow') {
      next.from = remapEndpoint(shape.from, remap);
      next.to = remapEndpoint(shape.to, remap);
    }
    return next as unknown as Shape;
  });
}

function remapEndpoint(
  ep: { kind: 'point'; x: number; y: number } | { kind: 'shape'; shapeId: string },
  remap: Map<string, string>,
): typeof ep {
  if (ep.kind !== 'shape') return ep;
  const next = remap.get(ep.shapeId);
  if (!next) return ep;
  return { kind: 'shape', shapeId: next };
}
